import React, { useState } from 'react';
import { Play, Lock } from 'lucide-react';

interface Video {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  url: string;
  duration: string;
  isPremium?: boolean;
}

interface VideoLibraryProps {
  title: string;
  description: string;
  videos: Video[];
  onPremiumContent: () => boolean;
}

export function VideoLibrary({ title, description, videos, onPremiumContent }: VideoLibraryProps) {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const handleVideoClick = (video: Video) => {
    if (video.isPremium && !onPremiumContent()) {
      return;
    }
    setSelectedVideo(video);
  };

  return (
    <div className="space-y-6">
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{selectedVideo.title}</h3>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                <video
                  controls
                  className="w-full h-full"
                  poster={selectedVideo.thumbnail}
                >
                  <source src="/path/to/video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="mt-4 text-gray-600">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-8 rounded-[30px] shadow-xl">
        <div>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">{title}</h2>
          <p className="text-gray-600">{description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {videos.map((video) => (
            <div
              key={video.id}
              className="group relative cursor-pointer"
              onClick={() => handleVideoClick(video)}
            >
              <div className="aspect-video rounded-xl overflow-hidden relative">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  {video.isPremium ? (
                    <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Lock className="text-white" size={24} />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-custom-orange-400">
                      <Play size={24} />
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  {video.title}
                  {video.isPremium && (
                    <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full">
                      Premium
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{video.description}</p>
                <span className="text-xs text-custom-orange-400 mt-2 block">
                  {video.duration}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}